<script setup>
import { MEDIA_CDN_BASE } from '@/utils/media'
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import _ from 'lodash'
import voca from 'voca'
import numeral from 'numeral'
import { Download } from 'lucide-vue-next'
import DiscoverTable from '@/partials/tables/DiscoverTable.vue'
import { formatThousands, formatWholeNumberWithUnit } from '@/utils/Utils'
import ColumnDisplayManager from '@/partials/options/ColumnDisplayManager.vue'
import DiscoveryProfileUnlock from '@/components/unlocks/DiscoveryProfileUnlock.vue'
import AppIcon from '@/components/AppIcon.vue'
import { DataTableFilterMenu } from '@/components/ui/data-table'
import NamePlate from '@/components/plates/NamePlate.vue'
import EmailUnlock from '@/components/unlocks/EmailUnlock.vue'

const store = useStore()
const router = useRouter()
const route = useRoute()

const props = defineProps({
    platformId: {
        type: String,
        required: false,
    },
    platformIds: {
        type: Array,
        required: false,
        default: () => [],
    },
    creators: {
        type: Array,
        required: true,
    },
    isLoading: {
        type: Boolean,
        default: false,
    },
    paging: {
        type: Object,
        required: true,
    },
    type: {
        type: String,
        required: false,
        defaultValue: () => 'platform',
    },
    availablePlatformOptions: {
        type: Array,
        required: false,
        default: () => [],
    },
    availableContentTypeOptions: {
        type: Array,
        required: false,
        default: () => [],
    },
    availableLanguageOptions: {
        type: Array,
        required: false,
        default: () => [],
    },
    availableCountryOptions: {
        type: Array,
        required: false,
        default: () => [],
    },
    reportTitle: {
        type: String,
        required: false,
    },
})

const emit = defineEmits(['paging-change', 'enabled-column-change', 'export'])

// Filtering state - single ref array like ContentTable
const filtering = ref([])

// Filter fields computed property matching ContentTable structure
const filterFields = computed(() => {
    const fields = []

    // Platform filter (only show if multiple platforms available)
    if (props.availablePlatformOptions.length > 1) {
        fields.push({
            label: 'Platform',
            value: 'platform_ids',
            options: props.availablePlatformOptions.map(option => ({
                label: option.label,
                value: option.value,
            })),
        })
    }

    // Content Type filter (only show if multiple content types available)
    if (props.availableContentTypeOptions.length > 1) {
        fields.push({
            label: 'Content Type',
            value: 'content_types',
            options: props.availableContentTypeOptions.map(option => ({
                label: option.label,
                value: option.value,
            })),
        })
    }

    // Sponsored status filter (always show)
    fields.push({
        label: 'Sponsored Status',
        value: 'is_sponsored',
        options: [{
            label: 'Organic',
            value: 'organic',
        }, {
            label: 'Sponsored',
            value: 'sponsored',
        }],
    })

    // Language filter (only show if options available)
    if (props.availableLanguageOptions.length > 0) {
        fields.push({
            label: 'Language',
            value: 'language',
            options: props.availableLanguageOptions.map(option => ({
                label: option.label,
                value: option.value,
            })),
        })
    }

    // Country filter (only show if options available)
    if (props.availableCountryOptions.length > 0) {
        fields.push({
            label: 'Country',
            value: 'country',
            options: props.availableCountryOptions.map(option => ({
                label: option.label,
                value: option.value,
            })),
        })
    }

    return fields
})

// Create filter values for API request - convert filtering array to filters format
const createFilterValues = () => {
    return filtering.value.map(({ id, value }) => ({ id, value }))
}

// Emit filter change with proper format
const emitFilterChange = () => {
    const filters = createFilterValues()

    emit('paging-change', {
        ...props.paging,
        currentPage: 1, // Reset to first page when filters change
        filters,
    })
}

// Platform filtering
const availablePlatforms = computed(() => {
    if (props.platformIds && props.platformIds.length > 0) {
        return props.platformIds
    }
    return props.platformId ? [props.platformId] : []
})

const effectivePlatformId = computed(() => {
    if (props.platformIds && props.platformIds.length > 0) {
        return props.platformIds.length === 1 ? props.platformIds[0] : null
    }
    return props.platformId
})

// Helper function to sort platforms consistently
const sortPlatforms = (platformIds) => {
    const order = ['youtube', 'twitch', 'tiktok', 'twitter', 'instagram']
    return [...platformIds].sort((a, b) => {
        const indexA = order.indexOf(a)
        const indexB = order.indexOf(b)
        // If platform not in order array, put it at the end
        if (indexA === -1 && indexB === -1) return 0
        if (indexA === -1) return 1
        if (indexB === -1) return -1
        return indexA - indexB
    })
}
const platforms = ['twitch', 'youtube', 'tiktok', 'twitter', 'instagram']
// Helper function to get matched platforms from a report item
const getMatchedPlatforms = (item) => {
    let platforms = []

    // For pivot-based reports, use platform_ids from data (this is the most reliable source)
    // This is set by the backend from actual matched content
    if (item.data?.platform_ids && Array.isArray(item.data.platform_ids) && item.data.platform_ids.length > 0) {
        platforms = item.data.platform_ids
    }
    // Extract from content items (deliverables) - shows platforms where content was actually matched
    else if (item.data?.deliverables && Array.isArray(item.data.deliverables)) {
        platforms = _.uniq(item.data.deliverables.map(content => content.platform_id).filter(Boolean))
    }
    // Also check item.deliverables directly (in case it's not nested in data)
    else if (item.deliverables && Array.isArray(item.deliverables)) {
        platforms = _.uniq(item.deliverables.map(content => content.platform_id).filter(Boolean))
    }
    // Extract from superviews if available (for pivot-based reports)
    else if (item.data?.superviews && Array.isArray(item.data.superviews)) {
        platforms = _.uniq(item.data.superviews.map(sv => sv.platform_id).filter(Boolean))
    }
    // Fallback to platform_id if single platform
    else if (item.platform_id || item.data?.platform_id) {
        platforms = [item.platform_id || item.data.platform_id]
    }
    // Extract from partialview if available
    else if (item.partialview?.platform_id || item.data?.partialview?.platform_id) {
        platforms = [item.partialview?.platform_id || item.data?.partialview?.platform_id]
    }
    // Last resort: if creator is in report, they must have content somewhere
    // Try to get from available platforms
    else if (availablePlatforms.value.length > 0) {
        platforms = availablePlatforms.value
    }
    // Should never reach here, but return empty array as fallback
    else {
        console.warn('Could not determine platforms for report item:', item)
        return []
    }

    // Sort platforms consistently
    return sortPlatforms(platforms)
}

// Filter creators by selected platforms - REMOVED: filtering is now done on backend
// The filteredCreators computed property is kept for backward compatibility but just returns all creators
const filteredCreators = computed(() => {
    return props.creators
})

// Helper function to check if a column should be enabled for a platform
const isColumnEnabledForPlatform = (platforms) => {
    if (effectivePlatformId.value === null && availablePlatforms.value.length > 0) {
        // Multi-platform: enable if any selected platform supports it
        return availablePlatforms.value.some(pid => platforms.includes(pid))
    }
    // Single platform: check if the platform is in the list
    const platformToCheck = effectivePlatformId.value || props.platformId
    return platforms.includes(platformToCheck)
}

const getPlatformDisplayName = (platformId) => (platformId === 'twitter' ? 'X' : voca.capitalize(platformId))

const getSummaryMetric = (item, field, fallbackField = field) => {
    const summaryValue = _.get(item, `summary.${field}`)
    return summaryValue ?? _.get(item, `facets.all.all.${fallbackField}`)
}

const formatMetric = (value) => value !== null && value !== undefined ? formatWholeNumberWithUnit(value) : '-'
const formatMetricForExport = (value) => value !== null && value !== undefined ? numeral(value).format('0,0') : ''
const formatEngagementRate = (value) => value !== null && value !== undefined ? numeral(value).format('0.0%') : '-'
const formatEngagementRateForExport = (value) => value !== null && value !== undefined ? numeral(value).format('0.0%') : ''

const getItemPlatformId = (item) => (
    _.get(item, 'partialview.platform_id') ||
    _.get(item, 'data.partialview.platform_id') ||
    _.get(item, 'platform_id') ||
    _.get(item, 'data.platform_id') ||
    effectivePlatformId.value
)

const getSharesLabel = (platformId = effectivePlatformId.value) => platformId === 'twitter' ? 'Total Reposts' : 'Total Shares'

const table = ref({
    enabledColumns: [],
    columns: [
        {
            id: 'partialview.normalized.name',
            key: 'partialview.normalized.name',
            header: {
                label: 'Creator',
                is_sortable: true,
                is_hideable: false,
                class: '',
            },
            row: {
                class: '',
                slot: 'creator',
                slotTransformer: (item) => ({
                    id: item.id,
                    name: item.partialview?.normalized?.name || item.data?.partialview?.normalized?.name,
                    display_name: item.partialview?.normalized?.display_name || item.data?.partialview?.normalized?.display_name,
                    logo: item.partialview?.normalized?.avatar_url || item.data?.partialview?.normalized?.avatar_url || `${MEDIA_CDN_BASE}/${item.partialview?.platform_id || item.data?.partialview?.platform_id || item.platform_id || item.data?.platform_id || effectivePlatformId.value || 'twitch'}/avatars/small/${item.id}`,
                    platform_id: item.partialview?.platform_id || item.data?.partialview?.platform_id || item.platform_id || item.data?.platform_id,
                }),
            },
            is_sticky: true,
            is_exportable: true,
            exportTransformer: (item) => ({
                Name: _.get(item, 'partialview.normalized.name'),
            }),
            enabled: () => {
                // For multi-platform reports, enable the column if any selected platform supports it
                if (effectivePlatformId.value === null && availablePlatforms.value.length > 0) {
                    return availablePlatforms.value.some(pid => ['twitch', 'youtube', 'tiktok', 'twitter', 'instagram'].includes(pid))
                }
                return ['twitch', 'youtube', 'tiktok', 'twitter', 'instagram'].includes(effectivePlatformId.value || '')
            },
            group : 'basic',
        },
        {
            id: 'matched_platforms',
            key: 'matched_platforms',
            header: {
                label: 'Content Profiles',
                is_sortable: false,
                tooltip: {
                    title: 'Platforms with Matched Content',
                    sub_title: '',
                    content: [
                        {
                            text: 'Shows which platforms this creator made content on that matched the report filters.',
                        },
                    ],
                },
            },
            row: {
                class: '',
                slot: 'platforms',
                slotTransformer: (item) => {
                    const platformsWithContent = getMatchedPlatforms(item)
                    const socials = item?.partialview?.normalized?.socials ?? {}

                    const platformLinksArray = Object.entries(socials)
                        .filter(([key]) => platformsWithContent.includes(key))
                        .map(([platform, social]) => ({
                            platform_id : social.platform_id,
                            platform_user_id : social.platform_user_id,
                            platform_user_name : social.platform_user_name,
                            display_name : social.display_name,
                            link: social.link,
                            avatar_url: social.avatar_url
                        }))
                    // Sort the array to prioritize the one matching this.platformId first
                    // Then follow the order from `platforms`
                    const platformSocials = platformLinksArray.sort((a, b) => {
                        if (a.platform_id === item.partialview?.platform_id) return -1
                        if (b.platform_id === item.partialview?.platform_id) return 1
                        return platformsWithContent.indexOf(a.platform_id) - platformsWithContent.indexOf(b.platform_id)
                    })
                    return { platformSocials }
                },
            },
            is_exportable: true, //Will be disabled if socials is selected
            enabled: () => {
                // Only show for multi-platform reports
                const isMultiPlatform = effectivePlatformId.value === null && availablePlatforms.value.length > 1
                return isMultiPlatform
            },
            exportTransformer: (item) => {
                const result = {}

                const platformsWithContent = getMatchedPlatforms(item)

                platforms.forEach((platformId) => {

                    let resultName = ''
                    let resultLink = ''

                    if (platformsWithContent.includes(platformId)) {
                        resultName = _.get(item, `partialview.normalized.socials.${platformId}.platform_user_name`, '')
                        resultLink = _.get(item, `partialview.normalized.socials.${platformId}.link`, '')
                    }

                    const platformName = getPlatformDisplayName(platformId)
                    result[platformName] = resultName
                    result[`${platformName} Link`] = resultLink
                })

                return result
            },
            group : 'basic',
        },
        {
            id: 'partialview.normalized.display_name',
            key: 'partialview.normalized.display_name',
            header: {
                label: 'Display Name',
                is_sortable: true,
                is_hideable: false,
                class: '',
            },
            row: {
                class: '',
                formatter: ({ field }) => field || '-',
            },
            is_exportable: true,
            exportTransformer: (item) => ({
                'Display Name': _.get(item, 'partialview.normalized.display_name'),
            }),
            enabled: () => true,
            group : 'basic',
        },
        {
            id: 'summary.avg_viewers',
            key: 'facets.all.all.avg_viewers',
            header: {
                label: 'Avg Viewers',
                sub_label: '',
                is_sortable: true,
                tooltip: {
                    title: 'Average Concurrent Viewers',
                    sub_title: '',
                    content: [
                        {
                            text: 'The average number of viewers you would expect to see in this creator\'s stream at any point within the aggregation window.',
                        },
                    ],
                },
            },
            row: {
                class: '',
                formatter: ({ field }) => field !== null && field !== undefined ? formatThousands(field) : '-',
            },
            is_exportable: true,
            exportTransformer: (item) => ({
                'Avg Viewers': numeral(_.get(item, 'facets.all.all.avg_viewers', 0)).format('0,0'),
            }),
            enabled: () => isColumnEnabledForPlatform(['twitch']),
            group : 'metrics',
        },
        {
            id: 'summary.peak_viewers',
            key: 'facets.all.all.peak_viewers.value',
            header: {
                label: 'Peak Viewers',
                sub_label: '',
                is_sortable: true,
                tooltip: {
                    title: 'Peak Concurrent Viewers',
                    sub_title: '',
                    content: [
                        {
                            text: 'The maximum number of viewers that were in this creator\'s stream at any point within the aggregation window.',
                        },
                    ],
                },
            },
            row: {
                class: '',
                formatter: ({ field }) => {
                    if (field !== null && field !== undefined) {
                        return formatWholeNumberWithUnit(field)
                    }
                    return '-'
                },
            },
            is_exportable: true,
            exportTransformer: (item) => ({
                'Peak Viewers': numeral(_.get(item, 'facets.all.all.peak_viewers.value', 0)).format('0,0'),
            }),
            enabled: () => isColumnEnabledForPlatform(['twitch']),
            group : 'metrics',
        },
        {
            id: 'summary.avg_commenters',
            key: 'facets.all.all.avg_commenters',
            header: {
                label: 'Avg Chatters',
                sub_label: '',
                is_sortable: true,
                tooltip: {
                    title: 'Average Concurrent Chatters',
                    sub_title: '',
                    content: [
                        {
                            text: 'The average number of chatters you would expect to see in this creator\'s stream at any point within the aggregation window.',
                        },
                    ],
                },
            },
            row: {
                class: '',
                formatter: ({ field }) => {
                    if (field !== null && field !== undefined) {
                        return formatWholeNumberWithUnit(field)
                    }
                    return '-'
                },
            },
            is_exportable: true,
            exportTransformer: (item) => ({
                'AVG Chatters': numeral(_.get(item, 'facets.all.all.avg_commenters', 0)).format('0,0'),
            }),
            enabled: () => isColumnEnabledForPlatform(['twitch']),
            group : 'engagement',
        },
        {
            id: 'summary.avg_comments',
            key: 'facets.all.all.avg_comments',
            header: {
                label: 'Avg Comments',
                sub_label: '',
                is_sortable: true,
                tooltip: {
                    title: 'Average Chat Messages',
                    sub_title: '',
                    content: [
                        {
                            text: 'The average number of chat messages per minute during this creator\'s streams.',
                        },
                    ],
                },
            },
            row: {
                class: '',
                formatter: ({ field }) => {
                    if (field !== null && field !== undefined) {
                        return formatWholeNumberWithUnit(field)
                    }
                    return '-'
                },
            },
            is_exportable: true,
            exportTransformer: (item) => ({
                'AVG Comments': numeral(_.get(item, 'facets.all.all.avg_comments', 0)).format('0,0'),
            }),
            enabled: () => isColumnEnabledForPlatform(['twitch']),
            group : 'engagement',
        },
        {
            id: 'summary.sum_content',
            key: 'facets.all.all.sum_content',
            header: {
                label: 'Content Count',
                sub_label: '',
                is_sortable: true,
                tooltip: {
                    title: 'Content count',
                    sub_title: '',
                    content: [
                        {
                            text: 'The total number of pieces of content that were matched.',
                        },
                    ],
                },
            },
            row: {
                class: '',
                formatter: ({ field }) => {
                    if (field !== null && field !== undefined) {
                        return formatWholeNumberWithUnit(field)
                    }
                    return '-'
                },
            },
            is_exportable: true,
            enabled: () => true,
            exportTarnsformer: (item) => ({
                'Content Count': numeral(_.get(item, 'facets.all.all.sum_content', 0)).format('0,0'),
            }),
            group : 'basic',
        },
        {
            id: 'summary.sum_duration_seconds',
            key: 'facets.all.all.sum_duration_seconds',
            header: {
                label: 'Hours Streamed',
                sub_label: '',
                is_sortable: true,
                tooltip: {
                    title: 'Hours Streamed',
                    sub_title: '',
                    content: [
                        {
                            text: 'The total number of hours streamed during the aggregation window.',
                        },
                    ],
                },
            },
            row: {
                class: '',
                formatter: ({ field }) => {
                    if (field !== null && field !== undefined) {
                        return formatWholeNumberWithUnit(field / 60 / 60)
                    }
                    return '-'
                },
            },
            is_exportable: true,
            enabled: () => isColumnEnabledForPlatform(['twitch']),
            exportTransformer: (item) => ({
                'Hours Streamed': formatWholeNumberWithUnit(_.get(item, 'facets.all.all.sum_duration_seconds', 0) / 60 / 60),
            }),
            group : 'metrics',
        },
        {
            id: 'partialview.normalized.followers',
            key: 'partialview.normalized.followers',
            header: {
                label: 'Followers',
                sub_label: '',
                is_sortable: true,
                tooltip: {
                    title: 'Followers',
                    sub_title: '',
                    content: [
                        {
                            text: 'The channel\'s current number of followers.',
                        },
                    ],
                },
            },
            row: {
                class: '',
                formatter: ({ field }) => {
                    if (field !== null && field !== undefined) {
                        return formatWholeNumberWithUnit(field)
                    }
                    return '-'
                },
            },
            is_exportable: true,
            enabled: () => {
                return true
            },
            exportTransformer: (item) => ({
                Followers: numeral(_.get(item, 'partialview.normalized.followers', 0)).format('0,0'),
            }),
            group : 'metrics',
        },
        {
            id: 'summary.avg_views',
            key: 'facets.all.all.avg_views',
            header: {
                label: 'AVG Views',
                sub_label: '',
                is_sortable: true,
                tooltip: {
                    title: 'Average views',
                    sub_title: '',
                    content: [
                        {
                            text: 'The average number of views per video during the period.',
                        },
                    ],
                },
            },
            row: {
                class: '',
                formatter: ({ field }) => {
                    if (field !== null && field !== undefined) {
                        return formatWholeNumberWithUnit(field)
                    }
                    return '-'
                },
            },
            is_exportable: true,
            enabled: () => isColumnEnabledForPlatform(['youtube', 'tiktok', 'twitter', 'instagram']),
            exportTransformer: (item) => ({
                'Avg Views': numeral(_.get(item, 'facets.all.all.avg_views', 0)).format('0,0'),
            }),
            group : 'metrics',
        },
        {
            id: 'summary.sum_views',
            key: 'facets.all.all.sum_views',
            header: {
                label: 'Total Views',
                sub_label: '',
                is_sortable: true,
                tooltip: {
                    title: 'Total views',
                    sub_title: '',
                    content: [
                        {
                            text: 'The total number of views for videos during the period.',
                        },
                    ],
                },
            },
            row: {
                class: '',
                formatter: ({ field }) => {
                    if (field !== null && field !== undefined) {
                        return formatWholeNumberWithUnit(field)
                    }
                    return '-'
                },
            },
            is_exportable: true,
            enabled: () => isColumnEnabledForPlatform(['twitch', 'youtube', 'tiktok', 'twitter', 'instagram']),
            exportTransformer: (item) => ({
                'Total Views': numeral(_.get(item, 'facets.all.all.sum_views', 0)).format('0,0'),
            }),
            group : 'metrics',
        },
        {
            id: 'summary.peak_views',
            key: 'facets.all.all.peak_views.value',
            header: {
                label: 'Peak Views',
                sub_label: '',
                is_sortable: true,
                tooltip: {
                    title: 'Peak views',
                    sub_title: '',
                    content: [
                        {
                            text: 'The peak number of views for videos during the period.',
                        },
                    ],
                },
            },
            row: {
                class: '',
                formatter: ({ field }) => {
                    if (field !== null && field !== undefined) {
                        return formatWholeNumberWithUnit(field)
                    }
                    return '-'
                },
            },
            is_exportable: true,
            enabled: () => isColumnEnabledForPlatform(['youtube', 'tiktok', 'twitter', 'instagram']),
            exportTransformer: (item) => ({
                'Peak Views': numeral(_.get(item, 'facets.all.all.peak_views.value', 0)).format('0,0'),
            }),
            group : 'metrics',
        },
        {
            id: 'summary.sum_minutes_watched',
            key: 'facets.all.all.sum_minutes_watched',
            header: {
                label: 'Total Hours Watched',
                sub_label: '',
                is_sortable: true,
                tooltip: {
                    title: 'Total Hours Watched',
                    sub_title: '',
                    content: [
                        {
                            text: 'The total number of hours of streaming content watched by the audience.',
                        },
                    ],
                },
            },
            row: {
                class: '',
                formatter: ({ field }) => {
                    if (field !== null && field !== undefined) {
                        return numeral(field / 60).format('0,0')
                    }
                    return '-'
                },
            },
            is_exportable: true,
            enabled: () => isColumnEnabledForPlatform(['twitch']),
            exportTransformer: (item) => ({
                'Total Hours Watched': numeral(_.get(item, 'facets.all.all.sum_minutes_watched', 0)
                    ? _.get(item, 'facets.all.all.sum_minutes_watched', 0) / 60
                    : 0).format('0,0'),
            }),
            group : 'metrics',
        },
        {
            id: 'summary.sum_shares',
            key: 'facets.all.all.sum_shares',
            header: {
                label: getSharesLabel(),
                sub_label: '',
                is_sortable: true,
                tooltip: {
                    title: getSharesLabel(),
                    sub_title: '',
                    content: [
                        {
                            text: 'The total number of shares across all content during the period.',
                        },
                    ],
                },
            },
            row: {
                class: '',
                formatter: ({ item }) => formatMetric(getSummaryMetric(item, 'sum_shares')),
            },
            is_exportable: true,
            enabled: () => isColumnEnabledForPlatform(['tiktok', 'twitter']),
            exportTransformer: (item) => ({
                [getSharesLabel(getItemPlatformId(item))]: formatMetricForExport(
                    getSummaryMetric(item, 'sum_shares'),
                ),
            }),
            group : 'engagement',
        },
        {
            id: 'summary.sum_quotes',
            key: 'summary.sum_quotes',
            header: {
                label: 'Total Quotes',
                sub_label: '',
                is_sortable: true,
                tooltip: {
                    title: 'Total Quotes',
                    sub_title: '',
                    content: [
                        {
                            text: 'The total number of quotes across all content during the period.',
                        },
                    ],
                },
            },
            row: {
                class: '',
                formatter: ({ item }) => formatMetric(getSummaryMetric(item, 'sum_quotes')),
            },
            is_exportable: true,
            enabled: () => isColumnEnabledForPlatform(['twitter']),
            exportTransformer: (item) => ({
                'Total Quotes': formatMetricForExport(getSummaryMetric(item, 'sum_quotes')),
            }),
            group : 'engagement',
        },
        {
            id: 'summary.sum_likes',
            key: 'facets.all.all.sum_likes',
            header: {
                label: 'Total Likes',
                sub_label: '',
                is_sortable: true,
                tooltip: {
                    title: 'Total Likes',
                    sub_title: '',
                    content: [
                        {
                            text: 'The total number of likes across all content during the period.',
                        },
                    ],
                },
            },
            row: {
                class: '',
                formatter: ({ field }) => {
                    if (field !== null && field !== undefined) {
                        return formatWholeNumberWithUnit(field)
                    }
                    return '-'
                },
            },
            is_exportable: true,
            enabled: () => isColumnEnabledForPlatform(['youtube', 'tiktok', 'twitter', 'instagram']),
            exportTransformer: (item) => ({
                'Total Likes': numeral(_.get(item, 'facets.all.all.sum_likes', 0)).format('0,0'),
            }),
            group : 'engagement',
        },
        {
            id: 'summary.sum_comments',
            key: 'facets.all.all.sum_comments',
            header: {
                label: 'Total Comments',
                sub_label: '',
                is_sortable: true,
                tooltip: {
                    title: 'Total Comments',
                    sub_title: '',
                    content: [
                        {
                            text: 'The total number of comments across all content during the period.',
                        },
                    ],
                },
            },
            row: {
                class: '',
                formatter: ({ field }) => {
                    if (field !== null && field !== undefined) {
                        return formatWholeNumberWithUnit(field)
                    }
                    return '-'
                },
            },
            is_exportable: true,
            enabled: () => isColumnEnabledForPlatform(['youtube', 'tiktok', 'twitter', 'instagram', 'twitch']),
            exportTransformer: (item) => ({
                'Total Comments': numeral(_.get(item, 'facets.all.all.sum_comments', 0)).format('0,0'),
            }),
            group : 'engagement',
        },
        {
            id: 'summary.avg_engagement_rate',
            key: 'summary.avg_engagement_rate',
            header: {
                label: 'Engagement Rate',
                sub_label: '',
                is_sortable: true,
                tooltip: {
                    title: 'Engagement Rate',
                    sub_title: '',
                    content: [
                        {
                            text: 'The percentage of viewers who engaged with the creator\'s content during the period.',
                        },
                    ],
                },
            },
            row: {
                class: '',
                formatter: ({ item }) => formatEngagementRate(_.get(item, 'summary.avg_engagement_rate')),
            },
            is_exportable: true,
            enabled: () => isColumnEnabledForPlatform(['twitch', 'youtube', 'tiktok', 'twitter', 'instagram']),
            exportTransformer: (item) => ({
                'Engagement Rate': formatEngagementRateForExport(_.get(item, 'summary.avg_engagement_rate')),
            }),
            group : 'engagement',
        },
        {
            id: 'email',
            key: 'partialview.normalized.email',
            header: {
                label: 'Email',
                is_sortable: false,
            },
            row: {
                class: '',
                slot: 'email',
            },
            is_exportable: true,
            enabled: () => true,
            exportTransformer: (item) => ({
                Email: _.get(item, 'partialview.normalized.email', ''),
            }),
            group : 'basic',
        },

        {
            id: 'language',
            key: 'partialview.normalized.language',
            header: {
                label: 'Language',
                is_sortable: false,
            },
            row: {
                class: '',
                formatter: ({ field }) => field && store.getters.getLanguageFromCode(field) !== 'Unknown' ? store.getters.getLanguageFromCode(field) : '',
            },
            is_exportable: true,
            enabled: () => isColumnEnabledForPlatform(['twitch', 'youtube', 'tiktok', 'twitter', 'instagram']),
            exportTransformer: (item) => ({
                Language: _.get(item, 'partialview.normalized.language', '') && store.getters.getLanguageFromCode(_.get(item, 'partialview.normalized.language', '')) !== 'Unknown' ? store.getters.getLanguageFromCode(_.get(item, 'partialview.normalized.language', '')) : '',
            }),
            group : 'basic',
        },
        {
            id: 'country',
            key: 'partialview.normalized.country',
            header: {
                label: 'Country',
                is_sortable: false,
            },
            row: {
                class: '',
                formatter: ({ field }) => field ? store.getters.getCountryFromCode(field) : '',
            },
            is_exportable: true,
            enabled: () => isColumnEnabledForPlatform(['twitch', 'youtube', 'tiktok', 'twitter', 'instagram']),
            exportTransformer: (item) => ({
                Country: _.get(item, 'partialview.normalized.country', '') ? store.getters.getCountryFromCode(_.get(item, 'partialview.normalized.country', '')) : '',
            }),
            group : 'basic',
        },
        {
            id: 'facets.all.all.sum_estimated_content_value',
            key: 'facets.all.all.sum_estimated_content_value',
            header: {
                label: 'Content Value',
                is_sortable: true,
                tooltip: {
                    title: 'Content Value',
                    sub_title: '',
                    content: [
                        {
                            text: 'The total estimated value of all content created by this creator, regardless of whether it was sponsored or organic.',
                        },
                    ],
                },
            },
            row: {
                class: '',
                formatter: ({ field }) => {
                    if (field !== null && field !== undefined) {
                        return `$${numeral(field).format('0,0')}`
                    }
                    return '-'
                },
            },
            is_exportable: true,
            enabled: () => props.type === 'platform',
            exportTransformer: (item) => ({
                'Content Value (USD)': `${numeral(_.get(item, 'facets.all.all.sum_estimated_content_value', 0)).format('0,0')}`,
            }),
            group : 'metrics',
        },
        {
            id: 'facets.by_sponsored.true.sum_estimated_sponsored_value',
            key: 'facets.by_sponsored.true.sum_estimated_sponsored_value',
            header: {
                label: 'Sponsored Value',
                is_sortable: true,
                tooltip: {
                    title: 'Sponsored Value',
                    sub_title: '',
                    content: [
                        {
                            text: 'The estimated value of only the sponsored content created by this creator. This is a subset of Content Value.',
                        },
                    ],
                },
            },
            row: {
                class: '',
                formatter: ({ field }) => {
                    if (field !== null && field !== undefined) {
                        return `$${numeral(field).format('0,0')}`
                    }
                    return '-'
                },
            },
            is_exportable: true,
            enabled: () => props.type === 'platform',
            exportTransformer: (item) => ({
                'Sponsored Value (USD)': `${numeral(_.get(item, 'facets.by_sponsored.true.sum_estimated_sponsored_value', 0)).format('0,0')}`,
            }),
            group : 'metrics',
        },
        {
            id: 'facets.all.all.avg_performance',
            key: 'facets.all.all.avg_performance',
            header: {
                label: 'Performance',
                is_sortable: true,
                tooltip: {
                    title: 'Average Performance',
                    sub_title: '',
                    content: [
                        {
                            text: 'How this creator\'s content performed compared to their 90-day average. Positive means above average, negative means below.',
                        },
                    ],
                },
            },
            row: {
                class: '',
                slot: 'performance',
            },
            is_exportable: true,
            enabled: () => props.type === 'platform',
            exportTransformer: (item) => ({
                'Performance': _.get(item, 'facets.all.all.avg_performance') != null
                    ? numeral(_.get(item, 'facets.all.all.avg_performance')).format('+0%')
                    : '',
            }),
            group : 'metrics',
        },
        {
            id: 'summary.sum_unique_clicks',
            key: 'summary.sum_unique_clicks',
            header: {
                label: 'Unique Clicks',
                is_sortable: true,
            },
            row: {
                class: '',
                formatter: ({ field }) => {
                    if (field !== null && field !== undefined) {
                        return `${numeral(field).format('0,0')}`
                    }
                    return '-'
                },
            },
            is_exportable: props.type === 'campaign',
            enabled: () => props.type === 'campaign',
            exportTransformer: (item) => ({
                'Unique Clicks': numeral(_.get(item, 'summary.sum_unique_clicks', 0)).format('0,0'),
            }),
            group : 'metrics',
        },
        {
            id: 'summary.cpc',
            key: 'summary.cpc',
            header: {
                label: 'CPC',
                is_sortable: true,
            },
            row: {
                class: '',
                formatter: ({ field }) => {
                    if (field !== null && field !== undefined) {
                        return `$${numeral(field).format('0,0.00')}`
                    }
                    return '-'
                },
            },
            is_exportable: props.type === 'campaign',
            enabled: () => props.type === 'campaign',
            exportTransformer: (item) => ({
                CPC: `$${numeral(_.get(item, 'summary.cpc', 0)).format('0,0.00')}`,
            }),
            group : 'metrics',
        },
        {
            id: 'summary.cpm',
            key: 'summary.cpm',
            header: {
                label: 'CPM',
                is_sortable: true,
            },
            row: {
                class: '',
                formatter: ({ field }) => {
                    if (field !== null && field !== undefined) {
                        return `$${numeral(field).format('0,0.00')}`
                    }
                    return '-'
                },
            },
            is_exportable: props.type === 'campaign',
            enabled: () => props.type === 'campaign',
            exportTransformer: (item) => ({
                CPM: `$${numeral(_.get(item, 'summary.cpm', 0)).format('0,0.00')}`,
            }),
            group : 'metrics',
        },
        {
            id: 'summary.ctr',
            key: 'summary.ctr',
            header: {
                label: 'CTR',
                is_sortable: true,
            },
            row: {
                class: '',
                formatter: ({ field }) => {
                    if (field !== null && field !== undefined) {
                        return numeral(field).format('0.0%')
                    }
                    return '-'
                },
            },
            is_exportable: props.type === 'campaign',
            enabled: () => props.type === 'campaign',
            exportTransformer: (item) => ({
                CTR: numeral(_.get(item, 'summary.ctr', 0)).format('0.0%'),
            }),
            group : 'metrics',
        },
        {
            id: 'summary.cost',
            key: 'summary.cost',
            header: {
                label: 'Total Cost',
                is_sortable: true,
            },
            row: {
                class: '',
                formatter: ({ field }) => {
                    if (field !== null && field !== undefined) {
                        return `$${numeral(field).format('0,0.00')}`
                    }
                    return '-'
                },
            },
            is_exportable: props.type === 'campaign',
            enabled: () => props.type === 'campaign',
            exportTransformer: (item) => ({
                'Total Cost': `$${numeral(_.get(item, 'summary.cost', 0)).format('0,0.00')}`,
            }),
            group : 'metrics',
        },
        {
            id: 'socials',
            key: 'socials',
            header: {
                label: 'Socials',
                is_sortable: false,
                class: 'w-32',
                tooltip: {
                    title: 'Social links',
                    sub_title: '',
                    content: [
                        {
                            text: 'Links to the creators social medial platforms.',
                        },
                    ],
                },
            },
            row: {
                class: '',
                slot: 'socials',
                slotTransformer: (item) => {
                    const socials = item?.partialview?.normalized?.socials || []
                    const platformLinksArray = Object.entries(socials)
                        .filter(([key]) => platforms.includes(key))
                        .map(([platformId, social]) => ({
                            platformId,
                            link: social.link,
                        }))
                    // Sort the array to prioritize the one matching this.platformId first
                    // Then follow the order from `platforms`
                    const platformLinks = platformLinksArray.sort((a, b) => {
                        // Prioritize the one matching this.platformId
                        if (a.platformId === item.partialview?.platform_id) return -1
                        if (b.platformId === item.partialview?.platform_id) return 1
                        // Otherwise follow the order from `platforms`
                        return platforms.indexOf(a.platformId) - platforms.indexOf(b.platformId)
                    })

                    return { platformLinks }
                },
            },
            enabled: () => isColumnEnabledForPlatform(['youtube', 'tiktok', 'twitter', 'instagram']),
            is_exportable: true,
            exportTransformer: (item) => {
                const result = {}
                platforms.forEach((platformId) => {
                    const platformName = getPlatformDisplayName(platformId)
                    result[platformName] = _.get(item, `partialview.normalized.socials.${platformId}.platform_user_name`, '')
                    result[`${platformName} Link`] = _.get(item, `partialview.normalized.socials.${platformId}.link`, '')
                })
                return result
            },
            group: 'basic',
        },
        // Audience Demographics - Gender
        {
            id: 'audience.gender.male',
            key: 'audience.gender.male',
            header: {
                label: 'Audience % Male',
                is_sortable: true,
                tooltip: {
                    title: 'Audience % Male',
                    content: [{ text: 'Percentage of this creator\'s audience that identifies as male, based on AI analysis of their content and comments.' }],
                },
            },
            row: {
                class: 'text-right tabular-nums',
                formatter: ({ field }) => field != null ? `${field}%` : '—',
            },
            is_exportable: props.type !== 'platform',
            enabled: () => props.type !== 'platform',
            exportTransformer: (item) => ({
                'Audience % Male': _.get(item, 'audience.gender.male') != null ? `${_.get(item, 'audience.gender.male')}%` : '',
            }),
            group: 'audience',
        },
        {
            id: 'audience.gender.female',
            key: 'audience.gender.female',
            header: {
                label: 'Audience % Female',
                is_sortable: true,
                tooltip: {
                    title: 'Audience % Female',
                    content: [{ text: 'Percentage of this creator\'s audience that identifies as female, based on AI analysis of their content and comments.' }],
                },
            },
            row: {
                class: 'text-right tabular-nums',
                formatter: ({ field }) => field != null ? `${field}%` : '—',
            },
            is_exportable: props.type !== 'platform',
            enabled: () => props.type !== 'platform',
            exportTransformer: (item) => ({
                'Audience % Female': _.get(item, 'audience.gender.female') != null ? `${_.get(item, 'audience.gender.female')}%` : '',
            }),
            group: 'audience',
        },
        {
            id: 'audience.gender.non_binary',
            key: 'audience.gender.non_binary',
            header: {
                label: 'Audience % Non-Binary',
                is_sortable: true,
                tooltip: {
                    title: 'Audience % Non-Binary',
                    content: [{ text: 'Percentage of this creator\'s audience that identifies as non-binary, based on AI analysis of their content and comments.' }],
                },
            },
            row: {
                class: 'text-right tabular-nums',
                formatter: ({ field }) => field != null ? `${field}%` : '—',
            },
            is_exportable: props.type !== 'platform',
            enabled: () => props.type !== 'platform',
            exportTransformer: (item) => ({
                'Audience % Non-Binary': _.get(item, 'audience.gender.non_binary') != null ? `${_.get(item, 'audience.gender.non_binary')}%` : '',
            }),
            group: 'audience',
        },

        // Audience Demographics - Age Ranges
        ...['13-17', '18-24', '25-34', '35-44', '45-54', '55+'].map((ageRange) => ({
            id: `audience.age_ranges.${ageRange}`,
            key: `audience.age_ranges.${ageRange}`,
            header: {
                label: `Audience ${ageRange}`,
                is_sortable: true,
                tooltip: {
                    title: `Audience Age ${ageRange}`,
                    content: [{ text: `Percentage of this creator's audience in the ${ageRange} age range.` }],
                },
            },
            row: {
                class: 'text-right tabular-nums',
                formatter: ({ field }) => field != null ? `${field}%` : '—',
            },
            is_exportable: props.type !== 'platform',
            enabled: () => props.type !== 'platform',
            exportTransformer: (item) => ({
                [`Audience Age ${ageRange}`]: _.get(item, `audience.age_ranges.${ageRange}`) != null ? `${_.get(item, `audience.age_ranges.${ageRange}`)}%` : '',
            }),
            group: 'audience',
        })),

        // Audience Demographics - Top Values
        {
            id: 'audience.top_country',
            key: 'audience.top_country.value',
            header: {
                label: 'Audience Top Country',
                is_sortable: true,
                tooltip: {
                    title: 'Audience Top Country',
                    content: [{ text: 'The most common country among this creator\'s audience.' }],
                },
            },
            row: {
                class: '',
                slot: 'audience-top-value',
                slotTransformer: (item) => _.get(item, 'audience.top_country'),
            },
            is_exportable: props.type !== 'platform',
            enabled: () => props.type !== 'platform',
            exportTransformer: (item) => {
                const data = _.get(item, 'audience.top_country')
                return { 'Audience Top Country': data ? `${data.value} (${data.percentage}%)` : '' }
            },
            group: 'audience',
        },
        {
            id: 'audience.top_language',
            key: 'audience.top_language.value',
            header: {
                label: 'Audience Top Language',
                is_sortable: true,
                tooltip: {
                    title: 'Audience Top Language',
                    content: [{ text: 'The most common language spoken by this creator\'s audience.' }],
                },
            },
            row: {
                class: '',
                slot: 'audience-top-value',
                slotTransformer: (item) => _.get(item, 'audience.top_language'),
            },
            is_exportable: props.type !== 'platform',
            enabled: () => props.type !== 'platform',
            exportTransformer: (item) => {
                const data = _.get(item, 'audience.top_language')
                return { 'Audience Top Language': data ? `${data.value} (${data.percentage}%)` : '' }
            },
            group: 'audience',
        },
        {
            id: 'audience.top_ethnicity',
            key: 'audience.top_ethnicity.value',
            header: {
                label: 'Audience Top Ethnicity',
                is_sortable: true,
                tooltip: {
                    title: 'Audience Top Ethnicity',
                    content: [{ text: 'The most common ethnicity among this creator\'s audience.' }],
                },
            },
            row: {
                class: '',
                slot: 'audience-top-value',
                slotTransformer: (item) => _.get(item, 'audience.top_ethnicity'),
            },
            is_exportable: props.type !== 'platform',
            enabled: () => props.type !== 'platform',
            exportTransformer: (item) => {
                const data = _.get(item, 'audience.top_ethnicity')
                return { 'Audience Top Ethnicity': data ? `${data.value} (${data.percentage}%)` : '' }
            },
            group: 'audience',
        },
        {
            id: 'audience.top_income',
            key: 'audience.top_income.value',
            header: {
                label: 'Audience Top Income',
                is_sortable: true,
                tooltip: {
                    title: 'Audience Top Income Bracket',
                    content: [{ text: 'The most common income bracket among this creator\'s audience.' }],
                },
            },
            row: {
                class: '',
                slot: 'audience-top-value',
                slotTransformer: (item) => _.get(item, 'audience.top_income'),
            },
            is_exportable: props.type !== 'platform',
            enabled: () => props.type !== 'platform',
            exportTransformer: (item) => {
                const data = _.get(item, 'audience.top_income')
                return { 'Audience Top Income': data ? `${data.value} (${data.percentage}%)` : '' }
            },
            group: 'audience',
        },
        {
            id: 'audience.top_education',
            key: 'audience.top_education.value',
            header: {
                label: 'Audience Top Education',
                is_sortable: true,
                tooltip: {
                    title: 'Audience Top Education Level',
                    content: [{ text: 'The most common education level among this creator\'s audience.' }],
                },
            },
            row: {
                class: '',
                slot: 'audience-top-value',
                slotTransformer: (item) => _.get(item, 'audience.top_education'),
            },
            is_exportable: props.type !== 'platform',
            enabled: () => props.type !== 'platform',
            exportTransformer: (item) => {
                const data = _.get(item, 'audience.top_education')
                return { 'Audience Top Education': data ? `${data.value} (${data.percentage}%)` : '' }
            },
            group: 'audience',
        },
        {
            id: 'audience.top_family_status',
            key: 'audience.top_family_status.value',
            header: {
                label: 'Audience Top Family Status',
                is_sortable: true,
                tooltip: {
                    title: 'Audience Top Family Status',
                    content: [{ text: 'The most common family status among this creator\'s audience.' }],
                },
            },
            row: {
                class: '',
                slot: 'audience-top-value',
                slotTransformer: (item) => _.get(item, 'audience.top_family_status'),
            },
            is_exportable: props.type !== 'platform',
            enabled: () => props.type !== 'platform',
            exportTransformer: (item) => {
                const data = _.get(item, 'audience.top_family_status')
                return { 'Audience Top Family Status': data ? `${data.value} (${data.percentage}%)` : '' }
            },
            group: 'audience',
        },
        {
            id: 'audience.top_engagement_style',
            key: 'audience.top_engagement_style.value',
            header: {
                label: 'Audience Engagement Style',
                is_sortable: true,
                tooltip: {
                    title: 'Audience Engagement Style',
                    content: [{ text: 'The most common engagement style among this creator\'s audience.' }],
                },
            },
            row: {
                class: '',
                slot: 'audience-top-value',
                slotTransformer: (item) => _.get(item, 'audience.top_engagement_style'),
            },
            is_exportable: props.type !== 'platform',
            enabled: () => props.type !== 'platform',
            exportTransformer: (item) => {
                const data = _.get(item, 'audience.top_engagement_style')
                return { 'Audience Engagement Style': data ? `${data.value} (${data.percentage}%)` : '' }
            },
            group: 'audience',
        },
    ],
    columnGroups: [
        { id: 'basic', label: 'Basic' },
        { id: 'metrics', label: 'Metrics' },
        { id: 'engagement', label: 'Engagement' },
        { id: 'audience', label: 'Audience' },
    ],
})
const platformColumns = computed(() => table.value.columns
    .filter((column) => column.enabled())
    .map((column) => {
        if (column.id !== 'summary.sum_shares') {
            return column
        }

        const label = getSharesLabel()
        return {
            ...column,
            header: {
                ...column.header,
                label,
                tooltip: {
                    ...column.header.tooltip,
                    title: label,
                },
            },
        }
    }))

const activeColumnGroups = computed(() => {
    if (props.type === 'platform') {
        return table.value.columnGroups.filter((g) => g.id !== 'audience')
    }
    return table.value.columnGroups
})

function setEnabledColumnDefaults() {
    const preferences = store.getters.preferences
    const platformKey = effectivePlatformId.value || 'multi-platform'
    if (preferences?.tables?.platformReport && preferences.tables.platformReport[platformKey]?.columns) {
        table.value.enabledColumns = preferences.tables.platformReport[platformKey].columns
    } else {
        const enabledCols = table.value.columns.filter((col) => col.enabled()).map((col) => col.id)
        // Always include matched_platforms column for multi-platform reports if it's enabled
        if (effectivePlatformId.value === null && availablePlatforms.value.length > 1) {
            if (!enabledCols.includes('matched_platforms')) {
                enabledCols.push('matched_platforms')
            }
        }
        table.value.enabledColumns = enabledCols
    }
}

function onEnabledColumnChange(newEnabledColumns) {
    table.value.enabledColumns = newEnabledColumns
    const platformKey = effectivePlatformId.value || 'multi-platform'
    store.commit('preferences:set', {
        key: `tables.platformReport.${platformKey}.columns`,
        value: table.value.enabledColumns,
    })
    emit('enabled-column-change', newEnabledColumns)
}

async function onExportItems(performItemsExport, calculateQuota) {

    let filteredEnabledColumns = _.filter(platformColumns.value, column => {
        return table.value.enabledColumns.includes(column.id)
    })

    let filteredAllColumns = _.filter(table.value.columns, column => column.is_exportable === true)

    if (table.value.enabledColumns.includes('socials')) {
        filteredAllColumns = _.filter(filteredAllColumns, column => {
            return column.id !== 'matched_platforms'
        })
        filteredEnabledColumns = _.filter(filteredEnabledColumns, column => {
            return column.id !== 'matched_platforms'
        })
    }

    let fileName = `Creators report - ${effectivePlatformId.value || 'multi-platform'}`

    if (props.reportTitle) {
        fileName = `Creators report - ${props.reportTitle}`.replace(/\//g, '').replace(/[^a-zA-Z0-9\s-_/\//]/g, '')
    }

    store.commit('modal:setData', {
        modal_id: 'export_table',
        data: {
            is_open: true,
            file_name: fileName,
            columns: {
                all: filteredAllColumns,
                enabled: filteredEnabledColumns,
            },
            checkerFn: async () => {
                if (!calculateQuota) {
                    return []
                }
                const reportResponse = await calculateQuota()

                if (reportResponse?.isQuotaError) {
                    return -1
                }

                return []
            },
            rowCount: props.paging.totalRecords,
            providerFn: async () => {
                const reportResponse = await performItemsExport()
                if (reportResponse?.isQuotaError) {
                    this.$errorHandler(reportResponse.error)
                    return -1
                }
                return reportResponse.data?.payload?.items
            },
        },
    })
}

// Create a minimal table-like wrapper object for DataTableFilterMenu compatibility
// DataTableFilterMenu expects a table object with getState() and setColumnFilters() methods
const tableWrapper = ref({
    getState: () => {
        // Convert filtering array to columnFilters format expected by DataTableFilterMenu
        const columnFilters = filtering.value.map(({ id, value }) => ({
            id,
            value: Array.isArray(value) ? value : [value],
        }))
        return {
            columnFilters,
        }
    },
    setColumnFilters: (columnFilters) => {
        // Convert columnFilters back to filtering array format
        filtering.value = columnFilters
            .filter(f => f.value && f.value.length > 0)
            .map(f => ({
                id: f.id,
                value: Array.isArray(f.value) ? f.value : [f.value],
            }))
        emitFilterChange()
    },
    getColumn: (columnId) => {
        // Return a minimal column object for compatibility
        // DataTableFilterMenu uses this to get/set individual filter values
        return {
            getFilterValue: () => {
                const filter = filtering.value.find(f => f.id === columnId)
                if (!filter) return []
                return Array.isArray(filter.value) ? filter.value : [filter.value]
            },
            setFilterValue: (value) => {
                const newFiltering = [...filtering.value]
                const existingIndex = newFiltering.findIndex(f => f.id === columnId)

                if (value && value.length > 0) {
                    const filterValue = {
                        id: columnId,
                        value: Array.isArray(value) ? value : [value]
                    }
                    if (existingIndex >= 0) {
                        newFiltering[existingIndex] = filterValue
                    } else {
                        newFiltering.push(filterValue)
                    }
                } else {
                    if (existingIndex >= 0) {
                        newFiltering.splice(existingIndex, 1)
                    }
                }

                filtering.value = newFiltering
                emitFilterChange()
            },
        }
    },
})

// Handle URL parameters for filtering
if (route.query['report.creators.filters']) {
    filtering.value = []
    const urlParameterFilters = Array.isArray(route.query['report.creators.filters'])
        ? route.query['report.creators.filters']
        : [route.query['report.creators.filters']]

    _.forEach(urlParameterFilters, (urlParameterFilter) => {
        const [id, value] = decodeURIComponent(urlParameterFilter).split(':')
        const values = value?.includes(',') ? value.split(',') : [value]
        filtering.value.push({
            id,
            value: values,
        })
    })
}

// Watch for changes and update URL
watch(
    () => [filtering],
    () => {
        router.push({
            query: {
                ...route.query,
                'report.creators.filters': _.map(filtering.value, ({ id, value }) => {
                    const valueStr = Array.isArray(value) ? value.join(',') : value
                    return `${encodeURIComponent(id)}:${encodeURIComponent(valueStr)}`
                }),
            },
        })
    },
    {
        immediate: false,
        deep: true,
    },
)

// Watch for changes in platformIds to re-enable columns
watch([() => props.platformIds, () => props.platformId], () => {
    setEnabledColumnDefaults()
}, { immediate: false })

// Initialize enabled columns
setEnabledColumnDefaults()

defineExpose({
    onExportItems,
})
</script>

<template>
    <div class="mt-4">
        <DiscoverTable
            :paging="paging"
            :creators="filteredCreators"
            :is-loading="isLoading"
            :columns="platformColumns"
            :enabled-columns="table.enabledColumns"
            :platform-id="effectivePlatformId"
            :is-selectable="true"
            id-prop="id"
            superview-path="partialview"
            @enabled-column-change="onEnabledColumnChange"
            @paging-change="(item) => $emit('paging-change', item)"
            @filter-change="(item) => $emit('paging-change', item)"
        >

            <template #header-toolbar>
                <ColumnDisplayManager
                    :available-columns="platformColumns"
                    :groups="activeColumnGroups"
                    :model-value="table.enabledColumns"
                    @update:model-value="(newEnabledColumns) => onEnabledColumnChange(newEnabledColumns)"
                />
            </template>

            <template #header-toolbar-filters>
                <DataTableFilterMenu
                    :table="tableWrapper"
                    :filter-fields="filterFields"
                />
            </template>

            <template #header-toolbar-right>
                <div>
                    <button
                        class="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-input bg-background px-4 py-1.5 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                        @click="$emit('export')"
                    >
                        <Download class="mr-2 h-4 w-4" />Export
                    </button>
                </div>
            </template>

            <template #creator="{ item }">
                <div class="relative flex w-full max-w-full items-center">
                    <DiscoveryProfileUnlock
                        :platform-id="item.partialview?.platform_id || item.data?.partialview?.platform_id || item.platform_id || item.data?.platform_id || effectivePlatformId || availablePlatforms[0] || 'twitch'"
                        :platform-user-id="item.partialview?._id || item.data?.partialview?._id || item.id"
                        :modal="'creator'"
                        :logo="item.partialview?.normalized?.avatar_url || item.data?.partialview?.normalized?.avatar_url"
                        :name="item.partialview?.normalized?.name || item.data?.partialview?.normalized?.name"
                        :display-name="item.partialview?.normalized?.display_name || item.data?.partialview?.normalized?.display_name"
                    />
                </div>
            </template>

            <template #email="{ item }">
                <EmailUnlock
                    v-if="item"
                    :platform-id="item.partialview?.platform_id"
                    :platform-user-id="item?.partialview?._id"
                />
            </template>

            <template #socials="{ field }">
                <div class="flex space-x-2">
                    <a
                        v-for="platform in field.platformLinks"
                        :key="platform.platformId"
                        :href="platform.link"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="flex items-center"
                    >
                        <AppIcon
                            :name="platform.platformId"
                            :class="`h-4 w-4 text-${platform.platformId}`"
                        />
                    </a>
                </div>
            </template>

            <template #platforms="{ field }">
                <div class="relative flex w-full gap-2">
                    <router-link
v-for="social in field.platformSocials" :key="social.platformId"
                        :to="{
                            path: $route.path,
                            query: {
                                ...$route.query,
                                modal: 'creator',
                                platform_id: social.platform_id,
                                platform_user_id: social.platform_user_id,
                            },
                        }"
                        class="align-center flex z-0"
                    >
                        <NamePlate
                            class="my-auto"
                            :logo="social.avatar_url"
                            :name="social.platform_user_name"
                            :display-name="social.display_name"
                            :platform-id="social.platform_id"
                            :platform-user-id="social.platform_user_id"
                            :is-drawer-enabled="false"
                            :is-interactive="true"
                            :hideName="true"
                        />
                    </router-link>
                </div>
            </template>

            <template #performance="{ item }">
                <div v-if="item.facets?.all?.all?.avg_performance != null" class="flex items-center justify-start">
                    <span
                        class="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 font-medium tabular-nums text-xs text-white"
                        :class="item.facets.all.all.avg_performance >= 0
                            ? 'bg-emerald-500'
                            : 'bg-rose-500'"
                    >
                        <AppIcon
                            :name="item.facets.all.all.avg_performance >= 0 ? 'arrow-trend-up' : 'arrow-trend-down'"
                            class="h-3 w-3"
                        />
                        {{ numeral(Math.abs(item.facets.all.all.avg_performance)).format('0%') }}
                    </span>
                </div>
                <span v-else class="text-slate-400">—</span>
            </template>

            <template #audience-top-value="{ field }">
                <div v-if="field && field.value" class="inline-flex items-center gap-1.5">
                    <span class="text-slate-700">{{ field.value }}</span>
                    <span class="text-xs text-slate-400">({{ field.percentage }}%)</span>
                </div>
                <span v-else class="text-slate-400">—</span>
            </template>
        </DiscoverTable>
    </div>
</template>
